// apps/web/src/pages/author/FieldArrayRow.tsx
//
// Phase 8 Plan 08 Task 1 — FieldArrayRow shared chrome (authr-01 / authr-03).
//
// Pure presentational component — NEVER reads form state. No FormContext
// subscription, no per-path watcher, no form-state proxy reads — these are
// AUTHR-06 anti-patterns (Pitfall 1 + Pitfall 2). The row's contents (form
// fields) are passed in as `children` from the section components; the
// chrome here is icon-cluster + Dialog only.
//
// Wraps content in a card-like row with Move-up / Move-down / Remove icon
// buttons at the right edge. Per UI-SPEC §"useFieldArray row" copy table
// (lines 337-341):
//   - Icon-only buttons (ChevronUp / ChevronDown / X lucide at 14px)
//   - aria-label = `Move ${singular} up` / `Move ${singular} down` /
//     `Remove ${singular}` (line 339-341)
//
// Remove opens a destructive-confirm Dialog per UI-SPEC §"Destructive
// confirmations" (lines 367-376). The Dialog title / body / confirm-label are
// caller-provided so each section can interpolate row-specific copy (W13:
// IngredientGroups uses `{group.label}` per the IngredientGroup schema, NOT
// `{group.name}` from the UI-SPEC wording — schema is source of truth).
//
// ACCESSIBILITY (UI-SPEC line 888):
//   - Dialog Cancel is rendered BEFORE Confirm so Radix Dialog's first-
//     focusable behavior auto-focuses Cancel — Enter on the Dialog defaults
//     to the safe action.
//   - Move-up disabled when canMoveUp=false (first row).
//   - Move-down disabled when canMoveDown=false (last row).

import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface FieldArrayRowProps {
  /** Singular name of the row's domain object (e.g., "ingredient", "instruction"). */
  singular: string;
  /** Zero-based row index, used in aria-labels disambiguation if needed. */
  index: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  /** Dialog title shown on Remove click (e.g., "Delete ingredient group?"). */
  deleteDialogTitle: string;
  /** Dialog body shown on Remove click (W13: callers interpolate row-specific
   *  values like `{group.label}` per the IngredientGroup schema). */
  deleteDialogBody: string;
  /** Destructive confirm-button label (e.g., "Delete group"). */
  deleteConfirmLabel: string;
  /** The row's editable content (FormField inputs scoped by the section). */
  children: React.ReactNode;
}

export function FieldArrayRow({
  singular,
  index: _index,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  deleteDialogTitle,
  deleteDialogBody,
  deleteConfirmLabel,
  children,
}: FieldArrayRowProps): React.ReactElement {
  const [removeOpen, setRemoveOpen] = useState(false);

  const handleConfirmRemove = (): void => {
    setRemoveOpen(false);
    onRemove();
  };

  return (
    <div className="border border-input rounded-md p-3 flex items-start gap-2">
      <div className="flex-1 min-w-0">{children}</div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          aria-label={`Move ${singular} up`}
          disabled={!canMoveUp}
          onClick={onMoveUp}
          className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronUp size={14} aria-hidden />
        </button>
        <button
          type="button"
          aria-label={`Move ${singular} down`}
          disabled={!canMoveDown}
          onClick={onMoveDown}
          className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronDown size={14} aria-hidden />
        </button>
        <button
          type="button"
          aria-label={`Remove ${singular}`}
          onClick={() => setRemoveOpen(true)}
          className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={14} aria-hidden />
        </button>
      </div>

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{deleteDialogTitle}</DialogTitle>
            <DialogDescription>{deleteDialogBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {/* Cancel rendered BEFORE Confirm so Radix's first-focusable
                rule auto-focuses the safe action (UI-SPEC line 888). */}
            <Button variant="outline" onClick={() => setRemoveOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemove}>
              {deleteConfirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
FieldArrayRow.displayName = "FieldArrayRow";
